// Boutique, Mon compte et Mes Miles : expérience membre « compte » du prototype TC.
// Composants réels de ubm-ui-components (imports granulaires) ; contenus repris des fixtures existantes (exchanges-fixtures,
// activities-fixtures), aucune donnée inventée. L'état vient d'un store hôte raccordé par connectAccountExperienceStore :
// la racine branche ses actions réelles (store Échanges aujourd'hui, adaptateur Foundry demain). Sans raccord, les fixtures
// s'affichent en lecture seule et toute action (achat, devis, réservation, préférences) est désactivée : aucun faux backend.
import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import {
  ArrowUpRight, ChevronRight, ClipboardCheck, Coins, Contact, Monitor, Moon, Presentation, Receipt, ShoppingBag, Sun,
} from "lucide-react";
import type { PageProps, Route } from "./contracts";
import { routeTitles, type ThemePreference } from "./Shell";
import { asset, MilesIcon } from "./shared";
import { eventVisual } from "./event-visuals";
import { Alert } from "../vendor/ubm-ui-components/src/components/alert";
import { AnimatedCounter } from "../vendor/ubm-ui-components/src/components/animated-counter";
import { Avatar } from "../vendor/ubm-ui-components/src/components/avatar";
import { Badge, type BadgeTone } from "../vendor/ubm-ui-components/src/components/badge";
import { BarChart } from "../vendor/ubm-ui-components/src/components/bar-chart";
import { Button } from "../vendor/ubm-ui-components/src/components/button";
import { CardSurface } from "../vendor/ubm-ui-components/src/components/card";
import { ChipGroup } from "../vendor/ubm-ui-components/src/components/chip-group";
import { EmptyState } from "../vendor/ubm-ui-components/src/components/empty-state";
import { Input } from "../vendor/ubm-ui-components/src/components/input";
import { RadioCards } from "../vendor/ubm-ui-components/src/components/radio-cards";
import { SegmentedControl } from "../vendor/ubm-ui-components/src/components/segmented-control";
import { Switch } from "../vendor/ubm-ui-components/src/components/switch";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow } from "../vendor/ubm-ui-components/src/components/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../vendor/ubm-ui-components/src/components/tabs";
import { Textarea } from "../vendor/ubm-ui-components/src/components/textarea";
import {
  ACCOUNT_NOTE, COIN_LABEL, GENIUS_NEXT, INITIAL_ACQUISITIONS, INITIAL_BALANCES, INITIAL_NOTIFICATIONS, INITIAL_REQUESTS, MEMBER, MEMBERSHIP,
  MILES_JOURNAL, MILES_NOTE, NOTIFICATION_PREFS, OFFERS, PAY_LABEL, SERVICES, capitalize, hourLabel, longDate, parseIso, serviceById,
  type Acquisition, type CoinKind, type MilesEntry, type Offer, type PayMethod, type QuoteRequest, type ServiceId,
} from "./exchanges-fixtures";
import { INITIAL_LEARN_PROGRESS, LEVELS, LIBERTY_LINKS, STEPS } from "./activities-fixtures";
import "./account-experience.css";

// ── Contrat hôte ─────────────────────────────────────────────────────────────

export interface AccountProfile {
  name: string;
  email: string;
  /** Statut affiché sous le nom, ex. « Membre Team Coaching ». */
  status: string;
  /** Accompagnement souscrit, ex. « Accompagnement Team Coaching · Niveau 1 inclus ». */
  program: string;
  /** Niveau d'apprentissage courant, ex. « Niveau 1 ». */
  level?: string;
  avatar?: string;
}
export interface AccountProfilePatch { name?: string; email?: string }

/** Instantané lu par les trois surfaces. Doit garder la même identité tant que rien ne change (useSyncExternalStore). */
export interface AccountExperienceSnapshot {
  profile: AccountProfile;
  /** Pièces de consultation disponibles, par type. */
  balances: Record<CoinKind, number>;
  acquisitions: readonly Acquisition[];
  requests: readonly QuoteRequest[];
  notifications: Record<string, boolean>;
  /** Registre des Miles (le solde en découle, jamais un compteur séparé). */
  milesJournal: readonly MilesEntry[];
  theme: ThemePreference;
}

type Ack = void | Promise<void>;
/**
 * Actions de l'hôte. Une promesse rejetée garde la saisie et affiche le refus ; le succès n'est affiché que lorsque
 * l'instantané reflète l'effet (acquisition, demande, préférence). Port absent : l'action correspondante est indisponible.
 */
export interface AccountExperienceActions {
  /** Acquisition d'une offre `buy` avec un seul mode de règlement. Aucun paiement réel dans le prototype. */
  acquireOffer?: (input: { offerId: string; pay: PayMethod }) => Ack;
  /** Demande de devis d'une offre `quote` : ne débite rien. */
  requestQuote?: (input: { offerId: string; text: string }) => Ack;
  /** Ouvre la réservation d'une consultation (parcours Coaching existant) ; l'hôte décide de la navigation. */
  bookService?: (service: ServiceId) => void;
  setNotification?: (id: string, enabled: boolean) => Ack;
  updateProfile?: (patch: AccountProfilePatch) => Ack;
  /** Préférence de thème du shell (Clair, Sombre, Système). */
  setTheme?: (theme: ThemePreference) => void;
}

export interface AccountExperienceStore {
  /** Identité stable (fonction liée) : elle sert de clé d'abonnement. */
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => AccountExperienceSnapshot;
  actions: AccountExperienceActions;
}

let host: AccountExperienceStore | null = null;
const hostListeners = new Set<() => void>();
const notifyHost = () => hostListeners.forEach(listener => listener());

/** Raccorde le store de la racine. Retourne la fonction de débranchement (retour au mode lecture seule). */
export function connectAccountExperienceStore(store: AccountExperienceStore): () => void {
  host = store;
  notifyHost();
  return () => { if (host === store) { host = null; notifyHost(); } };
}

const subscribeHost = (listener: () => void) => { hostListeners.add(listener); return () => { hostListeners.delete(listener); }; };
const getHost = () => host;

/** Niveau courant = niveau de l'étape qui contient la dernière leçon suivie dans Apprendre (même règle que le shell). */
const fixtureLevel = LEVELS.find(level => level.id === Object.values(STEPS).find(step => step.lessons.includes(INITIAL_LEARN_PROGRESS.last))?.level)?.title;

/** Lecture seule, figée une fois : identité stable. */
const FIXTURE_SNAPSHOT: AccountExperienceSnapshot = Object.freeze({
  profile: { name: MEMBER.name, email: MEMBER.email, status: MEMBERSHIP.label, program: MEMBERSHIP.program, level: fixtureLevel },
  balances: { ...INITIAL_BALANCES },
  acquisitions: INITIAL_ACQUISITIONS,
  requests: INITIAL_REQUESTS,
  notifications: { ...INITIAL_NOTIFICATIONS },
  milesJournal: MILES_JOURNAL,
  theme: "system" as ThemePreference,
});
const noSubscribe = () => () => {};
const getFixture = () => FIXTURE_SNAPSHOT;
const NO_ACTIONS: AccountExperienceActions = {};

function useAccount(): { s: AccountExperienceSnapshot; actions: AccountExperienceActions; connected: boolean } {
  const store = useSyncExternalStore(subscribeHost, getHost, getHost);
  const s = useSyncExternalStore(store?.subscribe ?? noSubscribe, store?.getSnapshot ?? getFixture, store?.getSnapshot ?? getFixture);
  return { s, actions: store?.actions ?? NO_ACTIONS, connected: !!store };
}

/** Une action asynchrone : un seul départ, refus affiché, saisie conservée. */
function usePending() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Ack, refusal: string) => {
    if (pending) return;
    setPending(true); setError(null);
    try { await fn(); } catch (cause) { setError(cause instanceof Error && cause.message ? cause.message : refusal); } finally { setPending(false); }
  };
  return { pending, error, run };
}

// ── Sections du compte (paramètre de hash) ───────────────────────────────────

export type AccountSection = "profile" | "preferences" | "appearance" | "purchases" | "miles";
export const ACCOUNT_SECTIONS: { value: AccountSection; label: string }[] = [
  { value: "profile", label: "Profil" }, { value: "preferences", label: "Préférences" }, { value: "appearance", label: "Apparence" },
  { value: "purchases", label: "Achats" }, { value: "miles", label: "Miles" },
];
const isSection = (value: string | null): value is AccountSection => ACCOUNT_SECTIONS.some(section => section.value === value);
/** Lien profond d'une section : #/account?section=purchases (le routeur racine retire la requête pour lire la route). */
export const accountSectionHash = (section: AccountSection) => section === "profile" ? "#/account" : `#/account?section=${section}`;

const SECTION_EVENT = "tc-account-section";
const readSection = (): AccountSection => {
  const value = new URLSearchParams(window.location.hash.split("?")[1] ?? "").get("section");
  return isSection(value) ? value : "profile";
};
const subscribeSection = (notify: () => void) => {
  window.addEventListener("hashchange", notify); window.addEventListener("popstate", notify); window.addEventListener(SECTION_EVENT, notify);
  return () => { window.removeEventListener("hashchange", notify); window.removeEventListener("popstate", notify); window.removeEventListener(SECTION_EVENT, notify); };
};
const onAccountRoute = () => window.location.hash.replace(/^#\/?/, "").split("?")[0] === "account";

/**
 * Ouvre une section du compte. Depuis une autre page : navigate("account") puis la requête est posée sur la même entrée
 * d'historique (retour = page d'origine). Depuis le compte : nouvelle entrée, le retour ramène à la section précédente.
 */
export function openAccountSection(navigate: (route: Route) => void, section: AccountSection) {
  if (onAccountRoute()) {
    if (readSection() !== section) window.history.pushState(null, "", accountSectionHash(section));
  } else {
    navigate("account");
    window.history.replaceState(null, "", accountSectionHash(section));
  }
  window.dispatchEvent(new Event(SECTION_EVENT));
}

// ── Présentation partagée ────────────────────────────────────────────────────

const fmt = (n: number) => n.toLocaleString("fr-FR");
const milesBalance = (journal: readonly MilesEntry[]) => journal.reduce((n, e) => n + (e.kind === "gain" ? e.amount : -e.amount), 0);
const shortDate = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short" });
const coinCount = (n: number, service: ServiceId) => { const sv = serviceById(service); return `${n} ${n > 1 ? sv.coinPlural : sv.coinName}`; };

function Section({ title, aside, children }: { title?: string; aside?: ReactNode; children: ReactNode }) {
  return <section className="acx-section">
    {title || aside ? <div className="acx-section-head">{title ? <h2 className="acx-h2">{title}</h2> : <span />}{aside}</div> : null}
    {children}
  </section>;
}

function Facts({ rows }: { rows: [string, ReactNode][] }) {
  return <dl className="acx-facts">{rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>;
}

const MilesGlyph = MilesIcon;

// ── Boutique : catalogue ─────────────────────────────────────────────────────

type ShopGroup = "included" | "consult" | "formation" | "events" | "services";
const SHOP_GROUPS: { id: ShopGroup; chip: string; title: string }[] = [
  { id: "included", chip: "Inclus", title: "Inclus dans ton accompagnement" },
  { id: "consult", chip: "Consultations", title: "Consultations" },
  { id: "formation", chip: "Formations", title: "Formations et replays" },
  { id: "events", chip: "Ateliers", title: "Ateliers en direct" },
  { id: "services", chip: "Outils et services", title: "Outils et services" },
];
const FILTERS = [{ value: "all", label: "Tout" }, ...SHOP_GROUPS.map(g => ({ value: g.id, label: g.chip }))];
const groupOf = (o: Offer): ShopGroup =>
  o.category === "consult" ? "consult" : o.category === "formation" || o.category === "replay" ? "formation" : o.category === "events" ? "events" : "services";

/** Visuels existants du prototype (affiches événements, niveaux, accès rapides). Aucun portrait, aucune image nouvelle. */
type Poster = { src: string } | { icon: ReactNode };
const posterIcon = (Glyph: typeof Receipt) => ({ icon: <Glyph size={40} strokeWidth={1.25} aria-hidden="true" /> });
const OFFER_POSTERS: Record<string, Poster> = {
  c1: { src: eventVisual("consult") }, m1: { src: eventVisual("consult") }, ar1: { src: eventVisual("consult") }, hs1: { src: eventVisual("consult") },
  f1: { src: "./assets/tc-da-level2-0810.png" }, r1: { src: "./assets/tc-da-formation-0810.png" },
  x1: { src: eventVisual("atelier") },
  a1: { src: "./assets/tc-da-audit-0810.png" }, s1: { src: "./assets/tc-da-crm-0810.png" }, s2: { src: "./assets/tc-da-presentation-0810.png" },
};
const posterOf = (id: string): Poster => OFFER_POSTERS[id] ?? posterIcon(ShoppingBag);

function PosterArt({ poster }: { poster: Poster }) {
  return "src" in poster ? <img className="acx-poster-img" src={poster.src} alt="" loading="lazy" /> : <span className="acx-poster-icon">{poster.icon}</span>;
}

/** Contenus inclus dans l'accompagnement : rien à acheter, ils ouvrent la page existante. */
interface IncludedItem { id: string; title: string; kicker: string; benefit: string; poster: Poster; route: Route; cta: string; facts: [string, string][] }
const level1 = LEVELS.find(level => level.access === "included");
const libertyMain = LIBERTY_LINKS.find(link => link.set);
const INCLUDED: IncludedItem[] = [
  { id: "inc-genius", title: "Team Genius", kicker: "Séance collective · incluse", poster: { src: eventVisual("tg") }, route: "accompagnement", cta: "Voir dans Coaching",
    benefit: GENIUS_NEXT.desc,
    facts: [["Prochaine séance", `${capitalize(longDate(GENIUS_NEXT.date))} · ${hourLabel(GENIUS_NEXT.start)}`], ["Thème", `${GENIUS_NEXT.theme} · ${GENIUS_NEXT.title}`]] },
  ...(level1 ? [{ id: "inc-level", title: `Formation ${level1.title}`, kicker: "Formation · incluse", poster: { src: "./assets/tc-da-level1-0810.png" }, route: "learn" as Route,
    cta: "Ouvrir dans Apprendre", benefit: level1.subtitle, facts: [["Accès", "Inclus dans ton accompagnement"], ["Format", "En ligne, dans Apprendre"]] as [string, string][] }] : []),
  { id: "inc-liberty", title: routeTitles.liberty, kicker: "Outil de suivi · inclus", poster: { src: "./assets/tc-da-liberty-0810.png" }, route: "liberty", cta: `Ouvrir ${routeTitles.liberty}`,
    benefit: "Tes documents et ton suivi préparés par l’équipe.", facts: libertyMain ? [["Document", libertyMain.title], ["Format", libertyMain.kind]] : [] },
];

type OfferStatus = { tone: BadgeTone; chip: string; line: string } | null;
function offerStatus(o: Offer, s: AccountExperienceSnapshot): OfferStatus {
  if (o.mode === "not_included") return { tone: "neutral", chip: "Non inclus", line: "Non inclus dans ton accompagnement actuel" };
  const acq = s.acquisitions.find(a => a.offer === o.id), req = s.requests.find(r => r.offer === o.id);
  if (acq?.state === "running") return { tone: "info", chip: "En cours", line: `Acquise le ${longDate(acq.date)} · prestation en cours, pas encore réalisée` };
  if (acq) return { tone: "success", chip: "Acquis", line: `Acquis le ${longDate(acq.date)} · ${PAY_LABEL[acq.pay].label}` };
  if (req) return { tone: "info", chip: "Demande envoyée", line: `Demande enregistrée le ${longDate(req.date)} · en attente de réponse de l’équipe` };
  if (o.service) {
    const sv = serviceById(o.service), b = s.balances[sv.coin];
    return { tone: b > 0 ? "success" : "neutral", chip: b > 0 ? coinCount(b, sv.id) : "À réserver", line: `${b > 0 ? `${coinCount(b, sv.id)} disponible${b > 1 ? "s" : ""}` : `Aucune ${sv.coinName} disponible`} · ou ${sv.price} € à la réservation` };
  }
  return null;
}
const offerMeta = (o: Offer) => o.mode === "not_included" ? "Non inclus dans ton accompagnement" : o.mode === "quote" ? "Sur devis"
  : o.mode === "buy" ? `Achat · ${o.pay!.map(p => PAY_LABEL[p].label).join(" ou ")}` : "Pièce ou euros, à la réservation";

function PosterCard({ poster, kicker, title, meta, badge, label, onOpen }: { poster: Poster; kicker: string; title: string; meta: ReactNode; badge?: ReactNode; label: string; onOpen: () => void }) {
  return <CardSurface asChild lift padding="none" className="acx-poster">
    <button type="button" onClick={onOpen} aria-label={label}>
      <span className="acx-poster-media"><PosterArt poster={poster} /></span>
      <span className="acx-poster-info">
        {badge}
        <span className="acx-poster-kicker">{kicker}</span>
        <span className="acx-poster-title">{title}</span>
        <span className="acx-poster-meta">{meta}</span>
      </span>
    </button>
  </CardSurface>;
}

function OfferCard({ offer, onOpen }: { offer: Offer; onOpen: () => void }) {
  const { s } = useAccount();
  const status = offerStatus(offer, s);
  const coin = offer.service ? serviceById(offer.service).coin : null;
  const service = offer.service ? serviceById(offer.service) : null;
  const balance = coin ? s.balances[coin] : 0;
  const badge = !service && status ? <Badge size="sm" tone={status.tone}>{status.chip}</Badge> : null;
  const price = service ? <span className="acx-price"><b>1</b><span className={`ex-coin ex-coin-${coin} ex-coin-md`} role="img" aria-label={COIN_LABEL[coin!]} /><span>ou</span><b>{service.price} €</b></span> : offerMeta(offer);
  return <PosterCard poster={posterOf(offer.id)} kicker={offer.kicker} title={offer.title} meta={price} badge={badge} onOpen={onOpen}
    label={`${offer.title}. ${offer.kicker}. ${service ? `1 ${service.coinName} ou ${service.price} €` : offerMeta(offer)}`} />;
}

// ── Boutique : détail d'offre (peek partagé) ─────────────────────────────────

function OfferDetail({ id, navigate }: { id: string; navigate: (route: Route) => void }) {
  const { s, actions } = useAccount();
  const o = OFFERS.find(x => x.id === id);
  const [pay, setPay] = useState<PayMethod | null>(o?.pay?.length === 1 ? o.pay[0]! : null);
  const [request, setRequest] = useState("");
  const buy = usePending(), quote = usePending();
  if (!o) return <EmptyState title="Offre introuvable" description="Cette offre n’est plus proposée." icon={false} />;
  const status = offerStatus(o, s);
  const acquired = s.acquisitions.find(a => a.offer === o.id);
  const requested = s.requests.find(r => r.offer === o.id);
  const unwired = null;

  return <div className="acx-stack">
    <div className="acx-peek-hero"><PosterArt poster={posterOf(o.id)} /></div>
    <p className="acx-kicker">{o.kicker}</p>
    <p className="acx-lead">{o.benefit}</p>
    <ul className="acx-bullets">{o.content.map(c => <li key={c}>{c}</li>)}</ul>
    <Facts rows={o.conditions} />
    {o.service ? <div className="acx-offer-price"><span className="acx-price"><b>1</b><span className={`ex-coin ex-coin-${serviceById(o.service).coin} ex-coin-md`} role="img" aria-label={serviceById(o.service).coinName} /><span>ou</span><b>{serviceById(o.service).price} €</b></span><span className="acx-small">{s.balances[serviceById(o.service).coin]} {serviceById(o.service).coinPlural} disponibles</span></div> : status ? <p className="acx-muted" role="status">{status.line}</p> : null}

    {o.mode === "not_included" ? <Alert tone="info" title="Non inclus dans ton accompagnement actuel">Aucun achat n’est proposé pour ce niveau. L’équipe peut t’en parler depuis Messages.</Alert> : null}

    {(o.mode === "credit" || o.mode === "book") && o.service ? <div className="acx-stack-sm">
      <Button disabled={!actions.bookService} onClick={() => actions.bookService?.(o.service!)}>Réserver un créneau</Button>

    </div> : null}

    {o.mode === "buy" && !acquired ? <div className="acx-stack-sm">
      <RadioCards aria-label="Règlement" layout="list" value={pay} onValueChange={v => setPay(v as PayMethod)}
        options={o.pay!.map(p => ({ value: p, label: PAY_LABEL[p].label, description: PAY_LABEL[p].sub, icon: p === "miles" ? <MilesGlyph /> : undefined }))} />
      <Button disabled={!pay || !actions.acquireOffer} loading={buy.pending}
        onClick={() => buy.run(() => actions.acquireOffer!({ offerId: o.id, pay: pay! }), "L’acquisition n’a pas pu être enregistrée. Ton choix est conservé.")}>
        Confirmer l’acquisition
      </Button>
      {buy.error ? <Alert tone="danger" title="Acquisition non enregistrée">{buy.error}</Alert> : null}
      {actions.acquireOffer ? <p className="acx-small">Un seul mode de règlement par acquisition.</p> : unwired}
    </div> : null}
    {o.mode === "buy" && acquired ? <Alert tone="success" title="Acquis">{o.opens ? "Le contenu est accessible." : "L’équipe te contacte pour la réalisation."}</Alert> : null}
    {acquired && o.opens && acquired.state === "access" ? <div className="acx-actions"><Button onClick={() => navigate(o.opens!.route)}>{o.opens.label}</Button></div> : null}

    {o.mode === "quote" && !requested ? <div className="acx-stack-sm">
      <Textarea label="Ta demande" rows={3} value={request} onChange={e => setRequest(e.target.value)} placeholder="Ce que tu attends, ta situation actuelle…" />
      <Button disabled={!request.trim() || !actions.requestQuote} loading={quote.pending}
        onClick={() => quote.run(() => actions.requestQuote!({ offerId: o.id, text: request.trim() }), "La demande n’a pas pu être envoyée. Ton texte est conservé.")}>
        Demander un devis
      </Button>
      {quote.error ? <Alert tone="danger" title="Demande non envoyée">{quote.error}</Alert> : null}
      {actions.requestQuote ? <p className="acx-small">Une demande ne débite rien et ne donne encore aucun droit.</p> : unwired}
    </div> : null}
    {o.mode === "quote" && requested ? <CardSurface variant="sunken" padding="sm"><p className="acx-small">Ta demande</p><p className="acx-p">{requested.text}</p></CardSurface> : null}
  </div>;
}

function IncludedDetail({ item, navigate }: { item: IncludedItem; navigate: (route: Route) => void }) {
  return <div className="acx-stack">
    <div className="acx-peek-hero"><PosterArt poster={item.poster} /></div>
    <p className="acx-kicker">{item.kicker}</p>
    <p className="acx-lead">{item.benefit}</p>
    {item.facts.length ? <Facts rows={item.facts} /> : null}
    <div className="acx-actions"><Button onClick={() => navigate(item.route)}>{item.cta}</Button></div>
  </div>;
}

const offerPeek = (o: Offer, navigate: (route: Route) => void) => ({ title: o.title, content: <OfferDetail id={o.id} navigate={navigate} /> });

// ── Boutique : page ──────────────────────────────────────────────────────────

export function ShopExperience({ navigate, openPeek }: PageProps) {
  const { s } = useAccount();
  const [filter, setFilter] = useState<string>("all");
  const linkedOffer = useSyncExternalStore(subscribeSection,
    () => new URLSearchParams(window.location.hash.split("?")[1] ?? "").get("offer") ?? "", () => "");
  const openedOffer = useRef("");
  useEffect(() => {
    if (!linkedOffer) { openedOffer.current = ""; return; }
    const offer = OFFERS.find(item => item.id === linkedOffer);
    if (!offer || openedOffer.current === linkedOffer) return;
    const frame = requestAnimationFrame(() => {
      openedOffer.current = linkedOffer;
      openPeek(offerPeek(offer, navigate));
    });
    return () => cancelAnimationFrame(frame);
  }, [linkedOffer, openPeek, navigate]);
  const visible = SHOP_GROUPS.filter(g => filter === "all" || g.id === filter);
  const purchases = s.acquisitions.length + s.requests.length;
  const openIncluded = (item: IncludedItem) => openPeek({ title: item.title, content: <IncludedDetail item={item} navigate={navigate} /> });

  return <div className="acx-page">
    <div className="acx-toolbar">
      <ChipGroup label="Catégories de la boutique" multiple={false} options={FILTERS} value={[filter]} onValueChange={v => setFilter(v[0] ?? "all")} />
      <Button variant="secondary" size="sm" onClick={() => openAccountSection(navigate, "purchases")} aria-label={`Mes achats, ${purchases} élément${purchases > 1 ? "s" : ""}`}>
        <Receipt size={16} strokeWidth={1.75} aria-hidden="true" />Mes achats{purchases ? <Badge size="sm" tone="neutral">{purchases}</Badge> : null}
      </Button>
    </div>
    {visible.map(group => <Section key={group.id} title={group.title}>
      <div className="acx-poster-grid">
        {group.id === "included"
          ? INCLUDED.map(item => <PosterCard key={item.id} poster={item.poster} kicker={item.kicker} title={item.title} meta={item.cta}
            badge={<span className="acx-badges"><Badge size="sm" tone="success">Inclus</Badge></span>} label={`${item.title}. ${item.kicker}.`} onOpen={() => openIncluded(item)} />)
          : OFFERS.filter(o => groupOf(o) === group.id).map(o => <OfferCard key={o.id} offer={o} onOpen={() => openPeek(offerPeek(o, navigate))} />)}
      </div>
    </Section>)}
  </div>;
}

// ── Carte membre ─────────────────────────────────────────────────────────────

function MemberHero({ s, action }: { s: AccountExperienceSnapshot; action?: ReactNode }) {
  const balance = milesBalance(s.milesJournal);
  return <CardSurface padding="lg" className="acx-member" data-theme="dark" aria-label={`Carte membre Team Coaching${s.profile.level ? `, ${s.profile.level}` : ""}, ${fmt(balance)} Miles disponibles`}>
    <div className="acx-member-top">
      <img className="acx-member-logo" src={asset("tc-logo-white.png")} alt="Team Coaching" />
      {s.profile.level ? <Badge tone="neutral" variant="outline">{s.profile.level}</Badge> : null}
    </div>
    <div className="acx-member-id">
      <Avatar size="lg" name={s.profile.name} src={s.profile.avatar} />
      <span className="acx-row-text"><b>{s.profile.name}</b><span>{s.profile.status}</span></span>
    </div>
    <div className="acx-member-foot">
      <span className="acx-member-balance">
        <span className="acx-member-coin"><MilesGlyph size={20} /></span>
        <span className="acx-row-text"><span>Miles disponibles</span><b className="acx-member-miles"><AnimatedCounter value={balance} locale="fr-FR" label="Miles disponibles" /></b></span>
      </span>
      {action}
    </div>
  </CardSurface>;
}

function MilesLedger({ journal, limit }: { journal: readonly MilesEntry[]; limit?: number }) {
  const rows = [...journal].sort((a, b) => b.date.localeCompare(a.date)).slice(0, limit);
  return <TableContainer label="Historique des Miles">
    <Table density="compact">
      <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Opération</TableHead><TableHead numeric>Miles</TableHead></TableRow></TableHeader>
      <TableBody>{rows.map(e => <TableRow key={e.id}>
        <TableCell>{capitalize(longDate(e.date))}</TableCell>
        <TableCell>{e.label}</TableCell>
        <TableCell numeric><span className="acx-amount" data-kind={e.kind}>{e.kind === "gain" ? "+" : "−"}{fmt(e.amount)}</span></TableCell>
      </TableRow>)}</TableBody>
    </Table>
  </TableContainer>;
}

// ── Mes Miles : page ─────────────────────────────────────────────────────────

export function MilesExperience({ navigate, openPeek }: PageProps) {
  const { s } = useAccount();
  const journal = s.milesJournal;
  const chrono = [...journal].sort((a, b) => a.date.localeCompare(b.date));
  const gained = journal.filter(e => e.kind === "gain").reduce((n, e) => n + e.amount, 0);
  const spent = journal.filter(e => e.kind === "spend").reduce((n, e) => n + e.amount, 0);
  const steps = [
    ...chrono.map(e => ({ key: e.id, label: `${e.label} · ${capitalize(longDate(e.date))}`, axisLabel: shortDate.format(parseIso(e.date)), value: e.kind === "gain" ? e.amount : -e.amount })),
    { key: "balance", label: "Solde actuel", axisLabel: "Solde", value: milesBalance(journal), total: true },
  ];
  const milesOffers = OFFERS.filter(o => o.pay?.includes("miles"));

  return <div className="acx-page acx-miles">
    <div className="acx-miles-main">
      <MemberHero s={s} />
      <Facts rows={[["Gagnés", `${fmt(gained)} Miles`], ["Utilisés", `${fmt(spent)} Miles`], ["Disponibles", `${fmt(gained - spent)} Miles`]]} />
      {journal.length ? <Section title="Évolution du solde">
        <BarChart presentation="waterfall" steps={steps} label="Évolution du solde de Miles" formatValue={n => `${fmt(n)} Miles`} height={220} />
      </Section> : null}
      <Section title="Utiliser tes Miles">
        <p className="acx-small">{MILES_NOTE}</p>
        <div className="acx-poster-grid acx-poster-grid-compact">{milesOffers.map(o => <OfferCard key={o.id} offer={o} onOpen={() => openPeek(offerPeek(o, navigate))} />)}</div>
      </Section>
    </div>
    <Section title="Historique">
      {journal.length ? <MilesLedger journal={journal} /> : <EmptyState title="Aucun mouvement" description="Tes Miles attribués et utilisés apparaîtront ici." icon={<MilesGlyph size={24} />} />}
    </Section>
  </div>;
}

// ── Mon compte : sections ────────────────────────────────────────────────────

function ProfileSection({ s, actions }: { s: AccountExperienceSnapshot; actions: AccountExperienceActions }) {
  const [draft, setDraft] = useState<AccountProfilePatch | null>(null);
  const save = usePending();
  const editable = !!actions.updateProfile;
  const name = draft?.name ?? s.profile.name, email = draft?.email ?? s.profile.email;
  const dirty = !!draft && (name !== s.profile.name || email !== s.profile.email);
  const valid = name.trim().length > 0 && /.+@.+\..+/.test(email.trim());
  return <div className="acx-stack">
    <div className="acx-profile-head">
      <Avatar size="xl" name={s.profile.name} src={s.profile.avatar} />
      <span className="acx-row-text"><b>{s.profile.name}</b><span>{s.profile.status}</span></span>
    </div>
    {editable ? <form className="acx-stack-sm" onSubmit={event => {
      event.preventDefault();
      if (!dirty || !valid) return;
      void save.run(async () => { await actions.updateProfile!({ name: name.trim(), email: email.trim() }); setDraft(null); }, "Le profil n’a pas pu être enregistré. Tes modifications sont conservées.");
    }}>
      <Input label="Nom" value={name} autoComplete="name" onChange={e => setDraft({ name: e.target.value, email })} />
      <Input label="E-mail" type="email" value={email} autoComplete="email" onChange={e => setDraft({ name, email: e.target.value })}
        error={email.trim() && !/.+@.+\..+/.test(email.trim()) ? "Adresse e-mail incomplète" : undefined} />
      {save.error ? <Alert tone="danger" title="Profil non enregistré">{save.error}</Alert> : null}
      <div className="acx-actions acx-actions-start">
        <Button type="submit" disabled={!dirty || !valid} loading={save.pending}>Enregistrer</Button>
        {dirty ? <Button type="button" variant="ghost" onClick={() => setDraft(null)}>Annuler</Button> : null}
      </div>
    </form> : <Facts rows={[["Nom", s.profile.name], ["E-mail", s.profile.email]]} />}
    <Facts rows={[["Accompagnement", s.profile.program], ...(s.profile.level ? [["Niveau en cours", s.profile.level] as [string, string]] : [])]} />
  </div>;
}

function PreferenceToggle({ id, label, sub, checked, onChange }: { id: string; label: string; sub: string; checked: boolean; onChange?: (id: string, v: boolean) => Ack }) {
  const toggle = usePending();
  return <div className="acx-toggle">
    <span className="acx-row-text"><b id={`acx-pref-${id}`}>{label}</b><span>{sub}</span>{toggle.error ? <span role="alert" className="acx-error">{toggle.error}</span> : null}</span>
    <Switch aria-labelledby={`acx-pref-${id}`} checked={checked} disabled={!onChange || toggle.pending}
      onCheckedChange={v => toggle.run(() => onChange!(id, v), "Préférence non enregistrée.")} />
  </div>;
}

function PreferencesSection({ s, actions }: { s: AccountExperienceSnapshot; actions: AccountExperienceActions }) {
  const list = (group: "families" | "reminders") => <div className="acx-toggle-list">
    {NOTIFICATION_PREFS.filter(p => p.group === group).map(p => <PreferenceToggle key={p.id} id={p.id} label={p.label} sub={p.sub} checked={!!s.notifications[p.id]} onChange={actions.setNotification} />)}
  </div>;
  return <div className="acx-stack">
    <Section title="Notifications">{list("families")}</Section>
    <Section title="Rappels d’accompagnement">{list("reminders")}</Section>
    <p className="acx-small">{ACCOUNT_NOTE}</p>
  </div>;
}

const THEME_OPTIONS = [
  { value: "light", label: "Clair", accessory: <Sun size={14} strokeWidth={1.75} aria-hidden="true" /> },
  { value: "dark", label: "Sombre", accessory: <Moon size={14} strokeWidth={1.75} aria-hidden="true" /> },
  { value: "system", label: "Système", accessory: <Monitor size={14} strokeWidth={1.75} aria-hidden="true" /> },
];

function AppearanceSection({ s, actions }: { s: AccountExperienceSnapshot; actions: AccountExperienceActions }) {
  return <div className="acx-stack">
    <Section title="Thème">
      <SegmentedControl label="Thème" options={THEME_OPTIONS} value={s.theme} disabled={!actions.setTheme} onValueChange={v => actions.setTheme?.(v as ThemePreference)} />
      <p className="acx-small">{actions.setTheme ? "« Système » suit le réglage clair ou sombre de ton appareil. Le même choix est disponible dans le menu de ton profil." : "Le thème se règle depuis le menu de ton profil."}</p>
    </Section>
  </div>;
}

function PurchaseRow({ offer, line, status, onOpen }: { offer: Offer; line: string; status?: OfferStatus; onOpen: () => void }) {
  return <CardSurface asChild lift padding="sm">
    <button type="button" className="acx-purchase" onClick={onOpen}>
      <span className="acx-purchase-thumb"><PosterArt poster={posterOf(offer.id)} /></span>
      <span className="acx-row-text"><b>{offer.title}</b><span>{line}</span></span>
      {status ? <Badge size="sm" tone={status.tone}>{status.chip}</Badge> : null}
      <ChevronRight size={16} strokeWidth={1.75} aria-hidden="true" className="acx-chevron" />
    </button>
  </CardSurface>;
}

function PurchasesSection({ s, actions, navigate, openPeek }: { s: AccountExperienceSnapshot; actions: AccountExperienceActions } & Pick<PageProps, "navigate" | "openPeek">) {
  const orders = s.acquisitions.map(a => ({ a, o: OFFERS.find(x => x.id === a.offer) })).filter((x): x is { a: Acquisition; o: Offer } => !!x.o);
  const requests = s.requests.map(r => ({ r, o: OFFERS.find(x => x.id === r.offer) })).filter((x): x is { r: QuoteRequest; o: Offer } => !!x.o);
  const credits = SERVICES.filter(sv => s.balances[sv.coin] > 0);
  const shopLink = <Button variant="secondary" size="sm" onClick={() => navigate("shop")}>Voir la Boutique<ArrowUpRight size={14} strokeWidth={1.75} aria-hidden="true" /></Button>;
  return <div className="acx-stack-lg">
    <Section title="Commandes" aside={shopLink}>
      {orders.length ? <div className="acx-list">{orders.map(({ a, o }) => <PurchaseRow key={o.id} offer={o} status={offerStatus(o, s)}
        line={`${capitalize(longDate(a.date))} · ${PAY_LABEL[a.pay].label}`} onOpen={() => openPeek(offerPeek(o, navigate))} />)}</div>
        : <EmptyState title="Aucune commande" description="Tes acquisitions de la Boutique apparaîtront ici." icon={<ShoppingBag size={24} strokeWidth={1.5} aria-hidden="true" />} />}
    </Section>
    <Section title="Demandes de devis">
      {requests.length ? <div className="acx-list">{requests.map(({ r, o }) => <PurchaseRow key={o.id} offer={o} status={offerStatus(o, s)}
        line={`Envoyée le ${longDate(r.date)} · en attente de réponse`} onOpen={() => openPeek(offerPeek(o, navigate))} />)}</div>
        : <EmptyState title="Aucune demande" description="Les offres sur devis se demandent depuis la Boutique." icon={false} />}
    </Section>
    <Section title="Pièces de consultation">
      {credits.length ? <div className="acx-list">{credits.map(sv => <CardSurface key={sv.id} padding="sm" className="acx-credit">
        <span className="acx-member-coin acx-coin-light"><MilesGlyph size={18} /></span>
        <span className="acx-row-text"><b>{sv.name}</b><span>{COIN_LABEL[sv.coin]} · {coinCount(s.balances[sv.coin], sv.id)} à utiliser</span></span>
        <Button size="sm" variant="secondary" disabled={!actions.bookService} onClick={() => actions.bookService?.(sv.id)}>Réserver</Button>
      </CardSurface>)}</div> : <EmptyState title="Aucune pièce disponible" description="Les consultations se règlent aussi en euros à la réservation." icon={false} />}
    </Section>
  </div>;
}

function MilesSummarySection({ s, navigate }: { s: AccountExperienceSnapshot; navigate: (route: Route) => void }) {
  return <div className="acx-stack">
    <MemberHero s={s} action={<Button size="sm" variant="secondary" onClick={() => navigate("miles")}>Historique complet</Button>} />
    <Section title="Derniers mouvements">
      {s.milesJournal.length ? <MilesLedger journal={s.milesJournal} limit={3} /> : <EmptyState title="Aucun mouvement" icon={false} />}
    </Section>
  </div>;
}

// ── Mon compte : page ────────────────────────────────────────────────────────

export function AccountExperience({ navigate, openPeek }: PageProps) {
  const { s, actions } = useAccount();
  const section = useSyncExternalStore(subscribeSection, readSection, () => "profile" as AccountSection);
  return <div className="acx-page">
    <Tabs value={section} onValueChange={v => openAccountSection(navigate, v as AccountSection)} className="acx-tabs">
      <TabsList aria-label="Sections du compte" className="acx-tablist">
        {ACCOUNT_SECTIONS.map(sec => <TabsTrigger key={sec.value} value={sec.value}>{sec.label}</TabsTrigger>)}
      </TabsList>
      <TabsContent value="profile"><ProfileSection s={s} actions={actions} /></TabsContent>
      <TabsContent value="preferences"><PreferencesSection s={s} actions={actions} /></TabsContent>
      <TabsContent value="appearance"><AppearanceSection s={s} actions={actions} /></TabsContent>
      <TabsContent value="purchases"><PurchasesSection s={s} actions={actions} navigate={navigate} openPeek={openPeek} /></TabsContent>
      <TabsContent value="miles"><MilesSummarySection s={s} navigate={navigate} /></TabsContent>
    </Tabs>
  </div>;
}

// ── Point d'entrée ───────────────────────────────────────────────────────────

/** Traite shop, account et miles ; toute autre route rend null (elle appartient à un autre module). */
export function ShopAccountExperience(props: PageProps) {
  switch (props.route) {
    case "shop": return <ShopExperience {...props} />;
    case "account": return <AccountExperience {...props} />;
    case "miles": return <MilesExperience {...props} />;
    default: return null;
  }
}

/** Routes servies par ce module, pour l'aiguillage de la racine. */
export const ACCOUNT_EXPERIENCE_ROUTES: readonly Route[] = ["shop", "account", "miles"];

export default ShopAccountExperience;
